import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { TanStackQueryIds } from '../../query/query-ids'
import type { ElemetalInfosData } from '~~/src/api/elementals'
import { saveElemental } from '~~/src/api/elementals'
import type { Elemental, ElementalTypeEnum } from '~~/src/types/elemental'
import type { List } from '~~/src/types/list'

function update(data: Elemental | undefined) {
  if (!data)
    return data
  data.isSaved = true
  data.nSaves = data.nSaves ?? 0
  return {
    ...data,
    nSaves: data.nSaves + 1,
  }
}
function updateInfo(data: ElemetalInfosData | undefined) {
  if (!data)
    return data
  return {
    ...data,
    isSaved: true,
  }
}

function revertUpdate(data: Elemental | undefined) {
  if (!data)
    return data
  data.isSaved = false
  data.nSaves = data.nSaves ?? 0
  return {
    ...data,
    nSaves: data.nSaves - 1,
  }
}
function revertInfo(data: ElemetalInfosData | undefined) {
  if (!data)
    return data
  return {
    ...data,
    isSaved: false,
  }
}

export const useSaveElemental = () => {
  const queryClient = useQueryClient()
  return useMutation<boolean, Error, { elementalType: ElementalTypeEnum; id: string; listId?: string; slug?: string }>({
    mutationKey: ['save-elemental'],
    mutationFn: async ({ id }) => {
      return await saveElemental(id)
    },
    onMutate: async ({ elementalType, id, listId, slug }) => {
      queryClient.setQueriesData({
        queryKey: ['elemental', elementalType, id],
      }, update)
      if (slug) {
        queryClient.setQueriesData({
          queryKey: ['elemental', elementalType, slug],
        }, update)
      }
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL_INFOS, id],
      }, updateInfo)
      if (!listId || listId === '0' || listId.toString().includes('all')) {
        const queryData = queryClient.getQueriesData({ queryKey: ['profile-list-elementals', elementalType], exact: false })
        if (!queryData || queryData.length < 1)
          return
        const [_, values] = queryClient.getQueriesData({ queryKey: ['profile-list-elementals', elementalType], exact: false })?.[0]
        if (values) {
          queryClient.setQueriesData<List[]>({
            queryKey: ['profile-list-elementals', elementalType],
            exact: false,
          }, (old) => {
            if (!old)
              return []
            return old.map((list) => {
              if ((list.id as unknown as number) === 0) {
                return {
                  ...list,
                  prompts: list.prompts ? list.prompts.filter(e => e.id !== id) : list.prompts,
                }
              }
              return list
            })
          })
        }
      }
      else {
        const [_, values] = queryClient.getQueriesData({ queryKey: ['list-elementals', listId], exact: false })[0]
        if (values) {
          queryClient.setQueriesData<Elemental[]>({
            queryKey: ['list-elementals', listId],
            exact: false,
          }, (old) => {
            if (!old || !old.length)
              return old
            return old.filter(e => e.id !== id)
          })
        }
      }
    },
    onError: (_, { elementalType, id, slug }) => {
      queryClient.setQueriesData({
        queryKey: ['elemental', elementalType, id],
      }, revertUpdate)
      if (slug) {
        queryClient.setQueriesData({
          queryKey: ['elemental', elementalType, slug],
        }, revertUpdate)
      }
      queryClient.setQueriesData({
        queryKey: [TanStackQueryIds.ELEMENTAL_INFOS, id],
      }, revertInfo)

      queryClient.refetchQueries({
        queryKey: ['profile-list-elementals', elementalType],
        exact: false,
        type: 'all',
      })
    },
    onSuccess: (_, { elementalType }) => {
      queryClient.refetchQueries({
        queryKey: ['profile-list-elementals', elementalType],
        exact: false,
        type: 'all',
      })
    },
  })
}
